import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductDescriptionRefs
 * @property {HTMLElement} content - The description content to measure and expand
 * @property {HTMLElement} toggleButton - The "Read more" / "Read less" control
 */

/**
 * Truncates the product description to a fixed collapsed height and toggles
 * between the full and collapsed states via the "Read more" / "Read less" control.
 *
 * @extends {Component<ProductDescriptionRefs>}
 */
class ProductDescriptionComponent extends Component {
  requiredRefs = ['content', 'toggleButton'];

  /**
   * @type {number}
   */
  #collapsedHeight = 300;

  /**
   * @constant {number}
   */
  #animationSpeed = 300;

  /**
   * @type {boolean}
   */
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#collapsedHeight = Number(this.dataset.collapsedHeight) || this.#collapsedHeight;
    this.#updateTruncation();
  }

  /**
   * Collapses the content to the configured height and only reveals the
   * toggle control when the content actually overflows that height.
   */
  #updateTruncation = () => {
    const { content, toggleButton } = this.refs;

    content.style.maxHeight = `${this.#collapsedHeight}px`;
    toggleButton.hidden = content.scrollHeight <= this.#collapsedHeight;
  };

  /**
   * Toggles between the collapsed and expanded states, animating the height change.
   */
  toggleExpanded = () => {
    const { content } = this.refs;
    const willExpand = !this.#expanded;
    const startHeight = content.getBoundingClientRect().height;
    const endHeight = willExpand ? content.scrollHeight : this.#collapsedHeight;

    content.style.maxHeight = 'none';

    const animation = content.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    animation.onfinish = () => {
      content.style.removeProperty('height');
      content.style.maxHeight = willExpand ? 'none' : `${this.#collapsedHeight}px`;
    };

    this.#expanded = willExpand;
    this.classList.toggle('product-description-component--expanded', this.#expanded);
  };
}

if (!customElements.get('product-description-component')) {
  customElements.define('product-description-component', ProductDescriptionComponent);
}
